import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  AlertTriangle, ArrowLeft, Bot, Check, Hand, Home, Loader2,
  MessageSquare, Play, Search, Send, ShieldCheck, Sparkles, UserPlus, X,
} from "lucide-react";
import ContactLink from "../contacts/components/ContactLink";
import { formatMelbourneDateTime } from "../../utils/melbourneTime";
import { searchContactsForList } from "../communications/api/communicationsApi";
import {
  decideGeeveeChange, fetchGeeveeConversation, fetchGeeveeConversations, fetchGeeveeEligible,
  fetchGeeveeMatches, fetchGeeveeOverview, geeveeConversationAction, sendGeeveeText, startGeevee,
} from "./api";

// GeeVee Conversations (Christian, Monday 13432357091): GeeVee texts buyers
// from one Collings number, reads their replies and lines up preference
// changes; agents read every thread, approve the changes and can take over.

const FILTERS = [
  { id: "needs_agent", label: "Needs you" },
  { id: "ready", label: "Ready to buy" },
  { id: "active", label: "GeeVee talking" },
  { id: "paused", label: "Agent took over" },
  { id: "completed", label: "Completed" },
  { id: "all", label: "All" },
];

const STATUS_PILL = {
  active: "bg-teal-50 text-teal-700",
  paused: "bg-blue-50 text-blue-700",
  handed_off: "bg-amber-50 text-amber-800",
  completed: "bg-gray-100 text-gray-600",
  opted_out: "bg-red-50 text-red-700",
  failed: "bg-red-50 text-red-700",
};

const LIST_POLL_MS = 20000;
const THREAD_POLL_MS = 10000;
const MAX_TEXT = 640;

const errorText = (e, fallback) => e?.response?.data?.detail || fallback;

const shortTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", timeZone: "Australia/Melbourne" });
  }
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", timeZone: "Australia/Melbourne" });
};

const StatusPill = ({ status, label }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${STATUS_PILL[status] || STATUS_PILL.completed}`}>
    {label}
  </span>
);

// ── Left: the list ──────────────────────────────────────────────────────────

function ConversationRow({ row, active, onSelect }) {
  const who = row.name || row.phone;
  const lastBy = row.last_message_author === "buyer" ? "" : row.last_message_author === "agent" ? "You: " : "GeeVee: ";
  return (
    <button
      type="button"
      onClick={() => onSelect(row.id)}
      className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55C5D0] ${
        active ? "bg-teal-50" : "hover:bg-gray-50"
      }`}
    >
      <div className="flex items-center gap-2">
        {row.unread && <span className="w-2 h-2 rounded-full bg-[#55C5D0] shrink-0" aria-label="Unread reply" />}
        <span className={`truncate text-sm ${row.unread ? "font-semibold text-gray-900" : "font-medium text-gray-800"}`}>{who}</span>
        <span className="ml-auto text-[11px] text-gray-400 tabular-nums shrink-0">{shortTime(row.last_message_at)}</span>
      </div>
      <p className={`mt-0.5 text-xs truncate ${row.unread ? "text-gray-800" : "text-gray-500"}`}>
        {lastBy}{row.last_message || "No messages yet"}
      </p>
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        <StatusPill status={row.status} label={row.status_label} />
        {row.pending_changes > 0 && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
            {row.pending_changes} to approve
          </span>
        )}
        {row.ready_to_buy && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700">
            <Sparkles className="w-3 h-3" /> Ready to buy
          </span>
        )}
      </div>
    </button>
  );
}

// ── Right: one conversation ─────────────────────────────────────────────────

function Bubble({ m }) {
  const inbound = m.direction === "in";
  const who = inbound ? "Buyer" : m.author === "agent" ? (m.sent_by || "Agent") : "GeeVee";
  const tone = inbound
    ? "bg-gray-100 text-gray-900"
    : m.author === "agent" ? "bg-blue-600 text-white" : "bg-[#E6F6F7] text-gray-900";
  let note = "";
  if (m.status === "queued") note = `Waiting for quiet hours to end, sends ${formatMelbourneDateTime(m.send_after)}`;
  else if (m.status === "failed") note = `Not delivered: ${m.error || "unknown error"}`;
  else if (m.status === "cancelled") note = `Not sent: ${m.error}`;
  else if (m.status === "delivered") note = "Delivered";
  return (
    <div className={`flex ${inbound ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[78%] ${m.status === "cancelled" ? "opacity-60" : ""}`}>
        <div className={`text-[11px] mb-0.5 text-gray-500 ${inbound ? "" : "text-right"}`}>
          {who} · {formatMelbourneDateTime(m.sent_at || m.created_at)}
        </div>
        <div className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${tone}`}>
          {m.body}
        </div>
        {note && (
          <div className={`text-[11px] mt-0.5 ${m.status === "failed" ? "text-red-600" : "text-gray-500"} ${inbound ? "" : "text-right"}`}>
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

function ChangesPanel({ conv, busyId, onDecide }) {
  const pending = conv.changes.filter((c) => c.status === "pending");
  const done = conv.changes.filter((c) => c.status === "applied" || c.status === "rejected");
  if (!pending.length && !done.length) {
    return <p className="text-xs text-gray-500">No preference changes from this conversation yet.</p>;
  }
  return (
    <section aria-label="Preference changes">
      <h3 className="text-sm font-semibold text-gray-900">
        {pending.length ? `${pending.length} preference change${pending.length === 1 ? "" : "s"} to approve` : "Preference changes"}
      </h3>
      <ul className="mt-2 space-y-1.5">
        {pending.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-end gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <div className="min-w-0 basis-full text-sm">
              <span className="font-medium text-gray-900">{c.label}</span>
              <span className="text-gray-500"> {c.current}</span>
              <span className="text-gray-400"> → </span>
              <span className="font-semibold text-gray-900">{c.suggested}</span>
            </div>
            <button
              type="button"
              disabled={busyId === c.id}
              onClick={() => onDecide(c, "reject")}
              className="px-2.5 py-1 text-xs font-semibold text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Not right
            </button>
            <button
              type="button"
              disabled={busyId === c.id}
              onClick={() => onDecide(c, "approve")}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-[#55C5D0] rounded-md hover:bg-[#45b5c0] disabled:opacity-50"
            >
              {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
            </button>
          </li>
        ))}
      </ul>
      {done.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {done.map((c) => (
            <li key={c.id} className="text-xs text-gray-500">
              {c.status === "applied" ? (
                <><Check className="inline w-3 h-3 text-green-600" /> {c.label} {c.suggested}, {c.auto_applied ? "saved by GeeVee" : `approved by ${c.decided_by}`}</>
              ) : (
                <><X className="inline w-3 h-3 text-gray-400" /> {c.label} {c.suggested}, turned down by {c.decided_by}</>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Matches({ conversationId }) {
  const [state, setState] = useState({ loading: false, data: null });
  const load = () => {
    setState({ loading: true, data: null });
    fetchGeeveeMatches(conversationId)
      .then((data) => setState({ loading: false, data }))
      .catch((e) => {
        setState({ loading: false, data: null });
        toast.error(errorText(e, "Could not load matching properties."));
      });
  };
  if (!state.data) {
    return (
      <button
        type="button"
        onClick={load}
        disabled={state.loading}
        className="inline-flex items-start gap-1.5 text-left text-xs font-semibold text-[#2B8F99] hover:underline disabled:opacity-60"
      >
        {state.loading ? <Loader2 className="w-3.5 h-3.5 mt-px shrink-0 animate-spin" /> : <Home className="w-3.5 h-3.5 mt-px shrink-0" />}
        Show properties that match their preferences
      </button>
    );
  }
  const items = state.data.items || [];
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-700">
        {items.length ? `Best matches (${state.data.total} in all)` : "Nothing matches their saved preferences yet"}
      </h4>
      <ul className="mt-1 space-y-0.5">
        {items.map((p) => (
          <li key={`${p.kind}-${p.id}`} className="text-xs text-gray-600 flex gap-2">
            <span className="font-semibold tabular-nums text-gray-900 w-8 shrink-0">{p.match_score ?? "-"}</span>
            <span className="truncate">{p.address}{p.suburb ? `, ${p.suburb}` : ""}</span>
            <span className="ml-auto text-gray-400 shrink-0">{p.column}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConversationView({ id, ready, onBack, onChanged }) {
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [acting, setActing] = useState("");
  const endRef = useRef(null);

  const load = useCallback(() => fetchGeeveeConversation(id).then(setConv), [id]);

  useEffect(() => {
    setConv(null);
    setText("");
    load().catch((e) => toast.error(errorText(e, "Could not open the conversation.")));
    const t = setInterval(() => load().catch(() => {}), THREAD_POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const count = conv?.messages?.length || 0;
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [count]);

  const apply = (data) => {
    setConv(data);
    onChanged();
  };

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      apply(await sendGeeveeText(id, body));
      setText("");
    } catch (err) {
      if (err?.response?.data?.conversation) apply(err.response.data.conversation);
      toast.error(errorText(err, "The text did not send."));
    } finally {
      setSending(false);
    }
  };

  const act = async (action, done) => {
    setActing(action);
    try {
      apply(await geeveeConversationAction(id, action));
      toast.success(done);
    } catch (err) {
      toast.error(errorText(err, "That did not work, please try again."));
    } finally {
      setActing("");
    }
  };

  const decide = async (change, decision) => {
    setBusyId(change.id);
    try {
      apply(await decideGeeveeChange(change.id, decision));
      toast.success(decision === "approve" ? `${change.label} saved to their preferences` : "Left their preferences as they were");
    } catch (err) {
      toast.error(errorText(err, "Could not save that change."));
      load().catch(() => {});
    } finally {
      setBusyId(null);
    }
  };

  if (!conv) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const closed = ["completed", "opted_out", "failed"].includes(conv.status);
  const canText = ready && conv.status !== "opted_out";
  const who = conv.name || conv.phone;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <header className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-start gap-x-3 gap-y-2">
        <button type="button" onClick={onBack} className="lg:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700" aria-label="Back to conversations">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1 basis-60">
          <div className="flex items-center gap-2 flex-wrap">
            {conv.contact_id ? (
              <ContactLink identity={{ contactId: conv.contact_id, name: who }} className="text-base font-semibold text-gray-900 hover:text-[#2B8F99] truncate">
                {who}
              </ContactLink>
            ) : (
              <span className="text-base font-semibold text-gray-900">{who}</span>
            )}
            <StatusPill status={conv.status} label={conv.status_label} />
            {conv.ready_to_buy && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700">
                <Sparkles className="w-3 h-3" /> Ready to buy
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            <span className="tabular-nums">{conv.phone}</span>
            {conv.agent ? ` · Agent: ${conv.agent}` : ""}
            {conv.status_reason ? ` · ${conv.status_reason}` : ""}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1 truncate">
            <ShieldCheck className="w-3 h-3 shrink-0" /> Why we may text them: {conv.contact_basis || "not recorded"}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-auto">
          {conv.status === "active" && (
            <button type="button" disabled={!!acting} onClick={() => act("pause", "You have taken over. GeeVee will stay quiet.")}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50">
              <Hand className="w-3.5 h-3.5" /> Take over
            </button>
          )}
          {(conv.status === "paused" || conv.status === "handed_off") && (
            <button type="button" disabled={!!acting || !ready} onClick={() => act("resume", "GeeVee has picked the conversation back up.")}
              title={ready ? "GeeVee carries on with its next question" : "GeeVee texting is not switched on"}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50 disabled:opacity-50">
              <Bot className="w-3.5 h-3.5" /> Hand back to GeeVee
            </button>
          )}
          {!closed && (
            <button type="button" disabled={!!acting} onClick={() => act("close", "Conversation closed.")}
              className="px-2.5 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              Close
            </button>
          )}
        </div>
      </header>

      {/* Wide screens: texts and reply box on the left, what came out of the
          conversation on the right. Narrower: the changes sit between the
          texts and the reply box, capped so the texts keep their room. */}
      <div className="flex-1 min-h-0 grid grid-rows-[minmax(0,1fr)_auto_auto] xl:grid-cols-[minmax(0,1fr)_320px] xl:grid-rows-[minmax(0,1fr)_auto]">
        <div className="min-h-0 overflow-y-auto px-4 py-4 space-y-3 xl:col-start-1 xl:row-start-1">
          {conv.messages.map((m) => <Bubble key={m.id} m={m} />)}
          <div ref={endRef} />
        </div>

        <aside className="max-h-36 overflow-y-auto border-t border-gray-200 bg-gray-50 px-4 py-3 space-y-3 xl:max-h-none xl:border-t-0 xl:border-l xl:col-start-2 xl:row-start-1 xl:row-span-2">
          <ChangesPanel conv={conv} busyId={busyId} onDecide={decide} />
          {conv.contact_id && <Matches key={conv.id} conversationId={conv.id} />}
        </aside>

        <form onSubmit={send} className="border-t border-gray-200 p-3 flex items-end gap-2 xl:col-start-1 xl:row-start-2">
          <div className="flex-1 min-w-0">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) send(e);
              }}
              rows={2}
              disabled={!canText}
              placeholder={
                !ready ? "GeeVee texting is not switched on yet"
                  : conv.status === "opted_out" ? "This buyer opted out of texts"
                    : conv.status === "paused" ? `Text ${who.split(" ")[0]}`
                      : `Text ${who.split(" ")[0]}. Sending takes over from GeeVee.`
              }
              className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent disabled:bg-gray-50"
            />
            <div className="text-[11px] text-gray-400 tabular-nums">
              {text.length}/{MAX_TEXT} · sent from the GeeVee number
            </div>
          </div>
          <button type="submit" disabled={!canText || sending || !text.trim()}
            className="mb-5 inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white bg-[#55C5D0] rounded-lg hover:bg-[#45b5c0] disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Starting the pilot (super admin) ────────────────────────────────────────

function StartPanel({ overview, onClose, onStarted }) {
  const [days, setDays] = useState(overview.lookback_days || 60);
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [starting, setStarting] = useState(false);
  const [results, setResults] = useState(null);
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);

  useEffect(() => {
    setData(null);
    fetchGeeveeEligible(days)
      .then(setData)
      .catch((e) => toast.error(errorText(e, "Could not load the pilot list.")));
  }, [days]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setFound([]);
      return undefined;
    }
    const t = setTimeout(() => searchContactsForList(q.trim()).then(setFound).catch(() => setFound([])), 300);
    return () => clearTimeout(t);
  }, [q]);

  const toggle = (id) => setPicked((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const start = async (ids) => {
    setStarting(true);
    try {
      const res = await startGeevee(ids);
      setResults(res.results);
      const ok = res.results.filter((r) => r.started).length;
      if (ok) toast.success(`GeeVee started ${ok} conversation${ok === 1 ? "" : "s"}`);
      setPicked(new Set());
      onStarted();
      fetchGeeveeEligible(days).then(setData).catch(() => {});
    } catch (e) {
      toast.error(errorText(e, "Could not start GeeVee."));
    } finally {
      setStarting(false);
    }
  };

  const buyers = data?.buyers || [];
  const leftOut = Object.entries(data?.left_out || {});
  const limit = overview.starts_left_today;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <header className="px-4 py-3 border-b border-gray-200 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-gray-900">Start GeeVee with buyers</h2>
          <p className="text-xs text-gray-500 mt-0.5 max-w-prose">
            Buyers who signed up to the portal or enquired in the last {days} days, with a mobile and no opt-out.
            Buyers with preferences come first. GeeVee texts between 9am and 8pm on weekdays and 9am to 5pm on Saturday;
            outside that the first text waits. {limit} more can start today.
          </p>
        </div>
        <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
      </header>

      <div className="px-4 py-3 border-b border-gray-200 space-y-2">
        <label className="block text-xs font-semibold text-gray-700" htmlFor="geevee-test-search">
          Test on an agent first: find the agent's own contact
        </label>
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input id="geevee-test-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or mobile"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]" />
        </div>
        {found.length > 0 && (
          <ul className="max-w-md border border-gray-200 rounded-lg divide-y divide-gray-100">
            {found.slice(0, 6).map((c) => (
              <li key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                <span className="truncate text-gray-900">{c.name}</span>
                <span className="text-xs text-gray-400 tabular-nums truncate">{c.phone}</span>
                <button type="button" disabled={starting} onClick={() => start([c.id])}
                  className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#2B8F99] hover:underline disabled:opacity-50">
                  <Play className="w-3 h-3" /> Start
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-[11px] text-gray-400">Only our own staff mobiles can be started without a sign-up or enquiry.</p>
      </div>

      {results && (
        <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs space-y-0.5">
          {results.map((r) => (
            <div key={r.contact_id} className={r.started ? "text-green-700" : "text-red-600"}>
              {r.name}: {r.started ? (r.queued_until ? `first text waits until ${formatMelbourneDateTime(r.queued_until)}` : "texted") : r.reason}
            </div>
          ))}
        </div>
      )}

      <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <label className="text-xs text-gray-600 flex items-center gap-1.5">
          Activity in the last
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="border border-gray-200 rounded-md px-1.5 py-1 text-xs">
            {[30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
        </label>
        <span className="text-xs text-gray-500">{data ? `${data.total} buyer${data.total === 1 ? "" : "s"} can be texted` : "Loading..."}</span>
        <button type="button" disabled={!picked.size || starting || picked.size > limit} onClick={() => start([...picked])}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#55C5D0] rounded-lg hover:bg-[#45b5c0] disabled:opacity-50">
          {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          Start GeeVee with {picked.size || ""} selected
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {!data ? (
          <div className="p-6 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : buyers.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">No buyers to text in this window.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white text-xs text-gray-500">
              <tr className="border-b border-gray-200">
                <th className="w-10 px-4 py-2" aria-label="Pick" />
                <th className="text-left font-semibold py-2 pr-3">Buyer</th>
                <th className="text-left font-semibold py-2 pr-3">Why we may text them</th>
                <th className="text-left font-semibold py-2 pr-4">Agent</th>
              </tr>
            </thead>
            <tbody>
              {buyers.map((b) => (
                <tr key={b.contact_id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => toggle(b.contact_id)}>
                  <td className="px-4 py-2">
                    <input type="checkbox" checked={picked.has(b.contact_id)} onChange={() => toggle(b.contact_id)}
                      onClick={(e) => e.stopPropagation()} aria-label={`Pick ${b.name}`} className="accent-[#55C5D0]" />
                  </td>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-gray-900">{b.name}</div>
                    <div className="text-xs text-gray-500 tabular-nums">
                      {b.phone}{b.has_preferences ? " · has preferences" : ""}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-600">{b.basis}</td>
                  <td className="py-2 pr-4 text-xs text-gray-600">{b.agent || "Unassigned"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {leftOut.length > 0 && (
          <div className="px-4 py-3 text-xs text-gray-500">
            <span className="font-semibold text-gray-600">Left out: </span>
            {leftOut.map(([reason, n]) => `${reason} (${n})`).join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}

// ── The tab ─────────────────────────────────────────────────────────────────

export default function GeeveeConversationsTab({ heightOffsetRem = 16.5 }) {
  const [overview, setOverview] = useState(null);
  const [filter, setFilter] = useState("needs_agent");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [starting, setStarting] = useState(false);

  const loadList = useCallback(
    () => fetchGeeveeConversations({ tab: filter, q: q.trim() || undefined }).then((d) => setRows(d.results)),
    [filter, q],
  );

  useEffect(() => {
    fetchGeeveeOverview().then(setOverview).catch(() => setOverview({ ready: false, not_ready_reason: "" }));
  }, []);

  useEffect(() => {
    const t0 = setTimeout(() => loadList().catch(() => setRows([])), q ? 300 : 0);
    const t = setInterval(() => loadList().catch(() => {}), LIST_POLL_MS);
    return () => {
      clearTimeout(t0);
      clearInterval(t);
    };
  }, [loadList, q]);

  const refresh = useCallback(() => {
    loadList().catch(() => {});
    fetchGeeveeOverview().then(setOverview).catch(() => {});
  }, [loadList]);

  const showRight = starting || activeId;
  const empty = useMemo(() => {
    if (filter === "needs_agent") return "Nothing needs you right now. Replies GeeVee cannot handle and changes to approve show here.";
    if (filter === "ready") return "No buyer has said they are finance-ready or buying within 3 months yet.";
    return "No conversations here.";
  }, [filter]);

  return (
    <div className="space-y-3">
      {overview && !overview.ready && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>{overview.not_ready_reason || "GeeVee texting is off."}</strong> Conversations can be read, but nothing is
            sent until the GeeVee number is set up and switched on.
          </p>
        </div>
      )}

      <div
        className="flex lg:grid lg:grid-cols-[320px_1fr] rounded-lg overflow-hidden border border-gray-200 bg-white min-h-0"
        style={{ height: `calc(100dvh - ${heightOffsetRem}rem)`, minHeight: "520px" }}
      >
        <aside className={`${showRight ? "hidden lg:flex" : "flex"} flex-col flex-1 lg:flex-none min-w-0 min-h-0 border-r border-gray-200`}>
          <div className="p-3 border-b border-gray-200 space-y-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4 text-[#55C5D0]" /> GeeVee texts
              </h2>
              {overview?.can_start && (
                <button type="button" onClick={() => { setStarting(true); setActiveId(null); }}
                  className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-[#55C5D0] rounded-lg hover:bg-[#45b5c0]">
                  <UserPlus className="w-3.5 h-3.5" /> Start
                </button>
              )}
            </div>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or mobile"
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]" />
            </div>
            <div className="flex gap-1 flex-wrap" aria-label="Filter conversations">
              {FILTERS.map((f) => (
                <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}
                  className={`px-2 py-1 rounded-md text-xs font-semibold transition-colors ${
                    filter === f.id ? "bg-teal-50 text-teal-700" : "text-gray-600 hover:bg-gray-100"
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-1.5 space-y-0.5">
            {rows === null ? (
              <div className="p-6 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            ) : rows.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">{empty}</p>
            ) : (
              rows.map((r) => (
                <ConversationRow key={r.id} row={r} active={r.id === activeId}
                  onSelect={(id) => { setActiveId(id); setStarting(false); }} />
              ))
            )}
          </div>
        </aside>

        <main className={`${showRight ? "flex" : "hidden lg:flex"} flex-col flex-1 min-w-0 min-h-0`}>
          {starting && overview ? (
            <StartPanel overview={overview} onClose={() => setStarting(false)} onStarted={refresh} />
          ) : activeId ? (
            <ConversationView id={activeId} ready={!!overview?.ready} onBack={() => setActiveId(null)} onChanged={refresh} />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
              <Bot className="w-8 h-8 text-[#55C5D0]" />
              <p className="mt-3 text-sm font-semibold text-gray-900">Pick a conversation</p>
              <p className="mt-1 text-xs text-gray-500 max-w-sm">
                GeeVee texts buyers from the Collings number, asks what is missing from their preferences and passes
                anything it cannot answer to you. Every text in and out is kept here.
              </p>
              <p className="mt-3 text-[11px] text-gray-400 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> A buyer who replies STOP is never texted again.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
